'use client'

import { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { motion } from 'framer-motion';
import styled, { createGlobalStyle } from 'styled-components';

import { jetbrainsMono, manrope } from './components/fonts';
import { useParallax } from './components/useParallax';
import { ACCENT, ACCENT_2, ACCENT_GRAD, BG, ON_ACCENT, TEXT, TEXT_2, TEXT_3 } from './components/constants';
import EarlyAccessForm from './components/EarlyAccessForm';
import { FEATURES } from './data';

import IconImg from '@/assets/img/metra/icon.png';
import HeroImg from '@/assets/img/metra/hero-timer.png';

const SESSION_MINUTES = 25;
const SESSION_TOTAL = SESSION_MINUTES * 60;

const formatClock = (seconds: number) => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

const Reveal = ({ children, className }: { children: React.ReactNode; className?: string }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 28, scale: 0.985, filter: 'blur(4px)' }}
    whileInView={{ opacity: 1, y: 0, scale: 1, filter: 'blur(0px)' }}
    viewport={{ once: true, margin: '-10% 0px -10% 0px' }}
    transition={{ duration: 0.72, ease: [0.22, 1, 0.36, 1] }}
  >
    {children}
  </motion.div>
);

const SoftReveal = ({ children, className, delay = 0 }: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 24, scale: 0.985 }}
    whileInView={{ opacity: 1, y: 0, scale: 1 }}
    viewport={{ once: true, margin: '-8% 0px -8% 0px' }}
    transition={{ duration: 0.72, delay, ease: [0.22, 1, 0.36, 1] }}
  >
    {children}
  </motion.div>
);

const HeroTimerChip = () => {
  const [left, setLeft] = useState(SESSION_TOTAL);

  useEffect(() => {
    const tick = setInterval(() => {
      setLeft((prev) => (prev > 0 ? prev - 1 : SESSION_TOTAL));
    }, 1000);

    return () => clearInterval(tick);
  }, []);

  const progress = (100 - (left / SESSION_TOTAL) * 100).toFixed(1);

  return (
    <ChipParallax data-parallax="-0.42" data-parallax-limit="380">
      <motion.div
        initial={{ opacity: 0, x: -46, y: 38, scale: 0.94, filter: 'blur(8px)' }}
        animate={{ opacity: 1, x: 0, y: 0, scale: 1, filter: 'blur(0px)' }}
        transition={{ duration: 0.9, delay: 0.62, ease: [0.22, 1, 0.36, 1] }}
      >
        <Chip>
          <div className="row">
            <span className="label">WORK SESSION</span>
            <span className="pause">
              <span className="bar" />
              <span className="bar" />
            </span>
          </div>
          <div className="clock">{formatClock(left)}</div>
          <div className="track">
            <div className="fill" style={{ width: `${progress}%` }} />
          </div>
        </Chip>
      </motion.div>
    </ChipParallax>
  );
};

const FloatingNav = () => {
  const [visible, setVisible] = useState(false);
  const lastY = useRef(0);
  const direction = useRef<1 | -1>(1);
  const travel = useRef(0);

  useEffect(() => {
    lastY.current = window.scrollY;

    const onScroll = () => {
      const y = window.scrollY;
      const diff = y - lastY.current;

      if (Math.abs(diff) < 0.5) return;

      const nextDirection: 1 | -1 = diff > 0 ? 1 : -1;
      if (nextDirection !== direction.current) {
        direction.current = nextDirection;
        travel.current = 0;
      }

      travel.current += Math.abs(diff);

      if (y < 180) {
        setVisible(false);
      } else if (nextDirection === 1 && travel.current > 12) {
        setVisible(false);
      } else if (nextDirection === -1 && travel.current > 18) {
        setVisible(true);
      }

      lastY.current = y;
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <>
      <FloatingBar $mode="hero" $visible>
        <a href="#top" className="brand">
          <Image src={IconImg} alt="Metra" width={26} height={26} className="mark" />
        </a>
        <div className="links">
          <a href="#features">Features</a>
        </div>
        <a href="#join" className="cta">Get early access</a>
      </FloatingBar>

      <FloatingBar $mode="floating" $visible={visible} aria-hidden={!visible}>
        <a href="#top" className="brand" tabIndex={visible ? 0 : -1}>
          <Image src={IconImg} alt="Metra" width={26} height={26} className="mark" />
        </a>
        <div className="links">
          <a href="#features" tabIndex={visible ? 0 : -1}>Features</a>
        </div>
        <a href="#join" className="cta" tabIndex={visible ? 0 : -1}>Get early access</a>
      </FloatingBar>
    </>
  );
};

const MetraLayout = () => {
  useParallax(true);

  useEffect(() => {
    const hasScrollRestoration = 'scrollRestoration' in window.history;
    const previous = hasScrollRestoration ? window.history.scrollRestoration : undefined;
    if (hasScrollRestoration) window.history.scrollRestoration = 'manual';

    // A stale #hash from earlier in-page navigation (e.g. clicking "Features")
    // survives a manual scroll back to top and makes a hard refresh jump
    // straight back to that section. Strip it so refresh always starts clean.
    if (window.location.hash) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }

    // Run after the browser's own native hash-scroll (which happens during
    // initial load, ahead of this effect) so ours reliably wins.
    const raf = requestAnimationFrame(() => window.scrollTo(0, 0));

    return () => {
      cancelAnimationFrame(raf);
      if (hasScrollRestoration && previous) window.history.scrollRestoration = previous;
    };
  }, []);

  return (
    <Main className={`${jetbrainsMono.variable} ${manrope.variable} ${manrope.className}`}>
      <SmoothScroll />
      <Grid />
      <Bloom data-parallax="-0.07" data-parallax-limit="120" className="top" />
      <Bloom data-parallax="0.1" data-parallax-limit="160" className="right" />
      <FloatingNav />

      <Hero id="top">
        <HeroHeadline data-parallax="-0.13" data-parallax-limit="150">
          <motion.div
            className="headlineEntrance"
            initial={{ opacity: 0, y: 42, scale: 0.975 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.95, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}
          >
            <h1>
              <span className="focusWord">Focus</span>, one<br />swipe away.
            </h1>
          </motion.div>
        </HeroHeadline>

        <HeroVisual>
          <div className="frame" data-parallax="-0.27" data-parallax-limit="310">
            <motion.div
              className="phoneEntrance"
              initial={{ opacity: 0, y: 90, scale: 0.94 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 1.15, delay: 0.38, ease: [0.22, 1, 0.36, 1] }}
            >
              <Image
                src={HeroImg}
                alt="Metra timer on the home screen"
                sizes="(min-width: 790px) 580px, 96vw"
                className="phoneBase"
                priority
              />
              <Image
                src={HeroImg}
                alt=""
                aria-hidden="true"
                sizes="(min-width: 790px) 580px, 96vw"
                className="phoneBlur"
              />
            </motion.div>
          </div>
          <HeroTimerChip />
        </HeroVisual>

        <HeroDetails data-parallax="-0.16" data-parallax-limit="210">
          <SoftReveal className="appIdentity">
            <Image src={IconImg} alt="" width={86} height={86} className="heroIcon" />
            <span>Metra</span>
          </SoftReveal>
          <SoftReveal className="statusReveal" delay={0.08}>
            <StatusPill>
              <span className="dot" />
              Now inviting early testers
            </StatusPill>
          </SoftReveal>

          <SoftReveal className="copyReveal" delay={0.14}>
            <p className="lead">
              Start in seconds, see what matters, and get back to the work.
            </p>
          </SoftReveal>

          <SoftReveal className="ctas" delay={0.2}>
            <a href="#join" className="primary">Join the beta</a>
            <a href="#features" className="ghost">See it in motion</a>
          </SoftReveal>
        </HeroDetails>

      </Hero>

      <FeatureStack id="features">
        <Container className="head">
          <Reveal>
            <span className="eyebrow">Features</span>
            <h2>Everything you need. Nothing you don&apos;t.</h2>
          </Reveal>
        </Container>

        <div className="stack">
          {FEATURES.map(({ n, label, title, body, img, alt, pos, phone }, index) => (
            <div className="stackItem" style={{ top: `${88 + index * 18}px`, zIndex: index + 1 }} key={n}>
              <Container>
                <Reveal className={`card ${index % 2 === 1 ? 'isReversed' : ''}`}>
                  <div className={`thumb ${phone ? 'phoneShot' : ''} ${pos === '50% 100%' ? 'anchorTop' : pos === '50% 0%' ? 'anchorBottom' : 'anchorCenter'}`}>
                    <Image
                      src={img}
                      alt={alt}
                      sizes="(min-width: 1240px) 620px, (min-width: 790px) 52vw, 100vw"
                      style={{ objectPosition: pos }}
                    />
                  </div>
                  <div className="text">
                    <div className="meta">
                      <span className="index">{n}</span>
                      <span className="label">{label}</span>
                    </div>
                    <h3>{title}</h3>
                    <p>{body}</p>
                  </div>
                </Reveal>
              </Container>
            </div>
          ))}
        </div>
      </FeatureStack>

      <Closing>
        <Join id="join">
          <Container>
            <Panel>
              <div className="glow" />
              <div className="content">
                <span className="eyebrow">Early access</span>
                <h2>Be first in line.<br />Get 30% off.</h2>
                <p>
                  Drop your email for a TestFlight invite — free to try, plus 30% off
                  Metra when it officially launches. One email, no list.
                </p>

                <EarlyAccessForm />

                <div className="fineprint">iOS 26+ · Free during TestFlight</div>
              </div>
            </Panel>
          </Container>
        </Join>

        <Footer>
          <Container>
            <div className="brand">
              <Image src={IconImg} alt="" width={26} height={26} className="mark" />
              <span className="wordmark">Metra</span>
            </div>
            <div className="links">
              <a href="#features">Features</a>
              <a href="#join">Early access</a>
              <a href="mailto:metra-support@plaen.co">Support</a>
            </div>
            <div className="copyright">&copy; 2026 Metra</div>
          </Container>
        </Footer>
      </Closing>
    </Main>
  );
};

const SmoothScroll = createGlobalStyle`
  html,
  body {
    margin: 0;
    background: ${BG};
  }

  html {
    scroll-behavior: smooth;
  }

  body {
    overscroll-behavior-y: none;
  }

  @media (prefers-reduced-motion: reduce) {
    html {
      scroll-behavior: auto;
    }
  }
`;

const Main = styled.main`
  position: relative;
  overflow-x: clip;
  background: ${BG};
  min-height: 100vh;
  color: ${TEXT};
  font-family: var(--font-manrope), system-ui, sans-serif;
  -webkit-font-smoothing: antialiased;

  a {
    color: ${ACCENT_2};
    text-decoration: none;
  }

  a:hover {
    color: #ffb49c;
  }

  @keyframes metraPulse {
    0%, 100% {
      opacity: 0.55;
      transform: scale(1);
    }
    50% {
      opacity: 0.9;
      transform: scale(1.06);
    }
  }

  @keyframes focusUnderline {
    from {
      transform: scaleX(0);
    }
    to {
      transform: scaleX(1);
    }
  }
`;

const Grid = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background-image: linear-gradient(rgba(255, 146, 115, 0.055) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 146, 115, 0.055) 1px, transparent 1px);
  background-size: 88px 88px;
  mask-image: linear-gradient(180deg, rgba(0, 0, 0, 0.9), transparent 78%);
`;

const Bloom = styled.div`
  position: absolute;
  pointer-events: none;

  &.top {
    top: -240px;
    left: 50%;
    width: 1100px;
    height: 900px;
    margin-left: -550px;
    background: radial-gradient(closest-side, rgba(255, 122, 84, 0.3), rgba(255, 122, 84, 0.06) 55%, transparent 72%);
    filter: blur(20px);
  }

  &.right {
    top: 1400px;
    right: -280px;
    width: 820px;
    height: 820px;
    background: radial-gradient(closest-side, rgba(255, 150, 110, 0.16), transparent 70%);
    filter: blur(30px);
  }
`;

const Container = styled.div`
  max-width: 1240px;
  margin: 0 auto;
  padding: 0 20px;

  @media ${({ theme }) => theme.mediaQuery.smallTablet} {
    padding: 0 32px;
  }
`;

const FloatingBar = styled.div<{ $visible: boolean; $mode: 'hero' | 'floating' }>`
  position: ${({ $mode }) => ($mode === 'hero' ? 'absolute' : 'fixed')};
  top: ${({ $mode }) => ($mode === 'hero' ? 'clamp(36px, 6vh, 56px)' : '16px')};
  left: 50%;
  z-index: ${({ $mode }) => ($mode === 'hero' ? 20 : 60)};
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 8px 10px 8px 12px;
  border-radius: 999px;
  background: rgba(16, 15, 14, 0.86);
  backdrop-filter: blur(16px);
  border: 1px solid rgba(245, 238, 234, 0.12);
  box-shadow: 0 20px 40px -20px rgba(0, 0, 0, 0.7);
  transform: translate(-50%, ${({ $visible, $mode }) => ($mode === 'hero' || $visible ? '0' : '-180%')});
  transition: transform 0.35s cubic-bezier(0.22, 1, 0.36, 1);
  pointer-events: ${({ $visible, $mode }) => ($mode === 'hero' || $visible ? 'auto' : 'none')};

  .brand {
    display: flex;
  }

  .mark {
    width: 28px;
    height: 28px;
    border-radius: 7px;
    display: block;
  }

  .links {
    display: none;
    align-items: center;
    gap: 18px;
    font-size: 13.5px;
    color: ${TEXT_2};

    a {
      color: inherit;
    }

    @media ${({ theme }) => theme.mediaQuery.smallTablet} {
      display: flex;
    }
  }

  .cta {
    display: inline-flex;
    align-items: center;
    padding: 8px 16px;
    border-radius: 999px;
    background: ${TEXT};
    color: #0b0a09;
    font-size: 13.5px;
    font-weight: 600;
    white-space: nowrap;
    transition: background 0.2s ease;

    &:hover {
      background: #ffffff;
      color: #0b0a09;
    }
  }
`;

const Hero = styled.section`
  position: relative;
  width: 100%;
  overflow: visible;
  background:
    radial-gradient(ellipse 68% 28% at 50% 14%, rgba(255, 122, 84, 0.2), transparent 72%),
    linear-gradient(180deg, rgba(12, 11, 10, 0.24), rgba(8, 8, 7, 0.04));

  h1 {
    margin: 30px auto 0;
    max-width: 1040px;
    font-size: clamp(42px, 12vw, 124px);
        line-height: 1.04;
    letter-spacing: -0.055em;
    font-weight: 720;
    text-wrap: balance;

        .focusWord {
          position: relative;
          display: inline-block;
          isolation: isolate;

          &::after {
            content: '';
            position: absolute;
            z-index: -1;
            right: 0.02em;
            bottom: 0.075em;
            left: 0.02em;
            height: 0.115em;
        border-radius: 0;
        background: ${ACCENT};
        transform: scaleX(0);
        transform-origin: left center;
        animation: focusUnderline 0.5s 1.45s cubic-bezier(0.6, 0, 0.25, 1) forwards;
      }
    }
  }

  @media (prefers-reduced-motion: reduce) {
    h1 .focusWord::after {
      animation: none;
      transform: scaleX(1);
    }
  }

  .lead {
    margin: 28px auto 0;
    max-width: 610px;
    font-size: clamp(16.5px, 1.6vw, 20px);
    line-height: 1.55;
    color: ${TEXT_2};
    text-wrap: pretty;
  }

  .ctas {
    display: flex;
    flex-wrap: wrap;
    gap: 14px;
    justify-content: center;
    margin-top: 32px;
  }

  .primary {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 16px 28px;
    min-height: 52px;
    border-radius: 999px;
    background: ${ACCENT_GRAD};
    color: ${ON_ACCENT};
    font-size: 16px;
    font-weight: 700;
    box-shadow: 0 18px 44px -18px rgba(255, 122, 84, 0.9);
    transition: transform 0.2s ease;

    &:hover {
      color: ${ON_ACCENT};
      transform: translateY(-2px);
    }
  }

  .ghost {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 16px 26px;
    min-height: 52px;
    border-radius: 999px;
    border: 1px solid rgba(245, 238, 234, 0.16);
    color: rgba(245, 238, 234, 0.86);
    font-size: 16px;
    font-weight: 500;
    transition: border-color 0.2s ease, color 0.2s ease;

    &:hover {
      border-color: rgba(245, 238, 234, 0.4);
      color: #ffffff;
    }
  }
`;

const HeroHeadline = styled.div`
  position: relative;
  z-index: 2;
  will-change: transform;
  min-width: 0;
  min-height: min(470px, 52svh);
  padding: clamp(78px, 9vh, 96px) 20px clamp(16px, 2vh, 28px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;

  .headlineEntrance {
    width: 100%;
  }
`;

const HeroDetails = styled.div`
  position: relative;
  z-index: 3;
  isolation: isolate;
  will-change: transform;
  margin-top: clamp(-248px, -17vw, -168px);
  padding: clamp(30px, 4vh, 48px) 20px clamp(96px, 12vh, 136px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  text-align: center;

  &::before {
    content: '';
    position: absolute;
    z-index: 0;
    top: clamp(-150px, -10vw, -96px);
    left: 50%;
    width: min(980px, 100vw);
    height: clamp(430px, 42vw, 580px);
    pointer-events: none;
    background: radial-gradient(
      ellipse at 50% 38%,
      rgba(255, 122, 84, 0.14) 0%,
      rgba(255, 122, 84, 0.075) 30%,
      rgba(255, 122, 84, 0.025) 52%,
      transparent 74%
    );
    filter: blur(18px);
    transform: translateX(-50%);
  }

  > * {
    position: relative;
    z-index: 1;
  }

  .appIdentity {
    display: flex;
    align-items: center;
    gap: 18px;
    margin-bottom: 30px;

    span {
      font-size: clamp(30px, 2.8vw, 39px);
      font-weight: 600;
      line-height: 1;
      letter-spacing: -0.06em;
      color: transparent;
      background: linear-gradient(180deg, #fffaf7 18%, #d8cbc5 100%);
      background-clip: text;
      -webkit-background-clip: text;
      text-shadow: 0 12px 36px rgba(255, 122, 84, 0.12);
    }
  }

  .statusReveal {
    display: flex;
    justify-content: center;
  }

  .copyReveal {
    width: 100%;
  }

  .heroIcon {
    width: 86px;
    height: 86px;
    border-radius: 24px;
    box-shadow: 0 26px 70px -24px rgba(255, 122, 84, 0.72);
  }
`;

const StatusPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 8px 16px 8px 12px;
  border-radius: 999px;
  border: 1px solid rgba(255, 146, 115, 0.28);
  background: rgba(255, 122, 84, 0.08);
  font-family: var(--font-mono), monospace;
  font-size: clamp(9.5px, 2.6vw, 11.5px);
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #ffa98d;
  white-space: nowrap;

  @media ${({ theme }) => theme.mediaQuery.smallTablet} {
    gap: 12px;
    padding: 7px 14px 7px 10px;
    letter-spacing: 0.14em;
  }

  .dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: ${ACCENT};
    animation: metraPulse 2.4s ease-in-out infinite;
  }
`;

const HeroVisual = styled.div`
  position: relative;
  isolation: isolate;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  height: clamp(700px, 66vw, 930px);
  padding: 0 20px;
  overflow: visible;

  .frame {
    position: relative;
    z-index: 1;
    will-change: transform;
    width: 100%;
    max-width: 470px;
    filter: drop-shadow(0 50px 90px rgba(0, 0, 0, 0.55));

    .phoneEntrance {
      position: relative;
      width: 100%;

      &::after {
        content: '';
        position: absolute;
        z-index: 3;
        inset: 0;
        pointer-events: none;
        background: linear-gradient(
          180deg,
          transparent 26%,
          rgba(8, 8, 7, 0.08) 34%,
          rgba(8, 8, 7, 0.24) 41%,
          rgba(8, 8, 7, 0.5) 48%,
          rgba(8, 8, 7, 0.78) 55%,
          rgba(8, 8, 7, 0.96) 62%,
          ${BG} 68%
        );
      }
    }

    img {
      display: block;
      width: 100%;
      height: auto;
    }

    .phoneBase {
      position: relative;
      z-index: 1;
      -webkit-mask-image: linear-gradient(180deg, #000 0%, #000 38%, rgba(0, 0, 0, 0.88) 44%, rgba(0, 0, 0, 0.58) 50%, rgba(0, 0, 0, 0.24) 56%, transparent 62%);
      mask-image: linear-gradient(180deg, #000 0%, #000 38%, rgba(0, 0, 0, 0.88) 44%, rgba(0, 0, 0, 0.58) 50%, rgba(0, 0, 0, 0.24) 56%, transparent 62%);
    }

    .phoneBlur {
      position: absolute;
      z-index: 2;
      inset: 0;
      filter: blur(11px) brightness(0.42) saturate(0.68);
      transform: scale(1.02);
      -webkit-mask-image: linear-gradient(180deg, transparent 34%, rgba(0, 0, 0, 0.3) 40%, rgba(0, 0, 0, 0.6) 46%, rgba(0, 0, 0, 0.4) 52%, rgba(0, 0, 0, 0.14) 58%, transparent 63%);
      mask-image: linear-gradient(180deg, transparent 34%, rgba(0, 0, 0, 0.3) 40%, rgba(0, 0, 0, 0.6) 46%, rgba(0, 0, 0, 0.4) 52%, rgba(0, 0, 0, 0.14) 58%, transparent 63%);
    }

    @media ${({ theme }) => theme.mediaQuery.smallTablet} {
      max-width: clamp(470px, 46vw, 590px);
    }
  }

  @media (max-width: 767px) {
    height: 580px;
    padding-top: 0;

    .frame {
      width: min(118vw, 480px);
    }
  }
`;

const ChipParallax = styled.div`
  display: none;
  position: absolute;
  z-index: 4;
  left: max(24px, calc(50% - 430px));
  bottom: 40%;
  will-change: transform;

  @media ${({ theme }) => theme.mediaQuery.smallTablet} {
    display: block;
  }
`;

const Chip = styled.div`
  width: 270px;
  padding: 19px 21px 21px;
  border-radius: 24px;
  background: rgba(16, 15, 14, 0.86);
  backdrop-filter: blur(14px);
  border: 1px solid rgba(245, 238, 234, 0.12);
  box-shadow: 0 34px 70px -26px rgba(0, 0, 0, 0.95);

  .row {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .label {
    font-family: var(--font-mono), monospace;
    font-weight: 500;
    font-size: 10.5px;
    letter-spacing: 0.18em;
    color: rgba(245, 238, 234, 0.5);
  }

  .pause {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: ${TEXT};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 2px;
  }

  .bar {
    width: 2.5px;
    height: 9px;
    background: #0b0a09;
    border-radius: 1px;
  }

  .clock {
    margin-top: 10px;
    font-family: var(--font-manrope), sans-serif;
    font-weight: 500;
    font-size: 38px;
    letter-spacing: -0.055em;
    color: #ffffff;
    font-variant-numeric: tabular-nums;
  }

  .track {
    margin-top: 12px;
    height: 4px;
    border-radius: 999px;
    background: rgba(245, 238, 234, 0.14);
    overflow: hidden;
  }

  .fill {
    height: 100%;
    border-radius: 999px;
    background: ${ACCENT_GRAD};
  }
`;

const FeatureStack = styled.section`
  position: relative;
  margin-top: -84px;
  padding: 52px 0 60px;

  @media ${({ theme }) => theme.mediaQuery.smallTablet} {
    margin-top: -96px;
    padding: 78px 0 80px;
  }

  .head > div {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
  }

  .head .eyebrow {
    display: block;
    font-family: var(--font-mono), monospace;
    font-size: 11.5px;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: ${ACCENT_2};
  }

  .head h2 {
    margin: 18px 0 0;
    max-width: 780px;
    font-size: clamp(32px, 4.2vw, 58px);
    line-height: 1.02;
    letter-spacing: -0.035em;
    font-weight: 700;
    text-wrap: balance;

    @media (max-width: 767px) {
      font-size: clamp(40px, 3.5vw, 50px);
    }
  }

  .stack {
    margin-top: clamp(42px, 5vw, 64px);
  }

  .stackItem {
    position: sticky;
    padding-bottom: 40px;
  }

  .card {
    display: grid;
    grid-template-columns: minmax(360px, 0.84fr) minmax(460px, 1.16fr);
    grid-template-areas: 'text image';
    height: clamp(440px, 46vw, 560px);
    border-radius: 34px;
    overflow: hidden;
    border: 1px solid rgba(245, 238, 234, 0.1);
    background: linear-gradient(115deg, #121211 0%, #10100f 62%, #0c0c0b 100%);
    box-shadow: 0 30px 60px -30px rgba(0, 0, 0, 0.8);

    &.isReversed {
      grid-template-columns: minmax(460px, 1.16fr) minmax(360px, 0.84fr);
      grid-template-areas: 'image text';
      background: linear-gradient(245deg, #121211 0%, #10100f 62%, #0c0c0b 100%);
    }

    @media (max-width: 767px) {
      grid-template-columns: 1fr;
      grid-template-areas: 'text' 'image';
      height: auto;
      border-radius: 26px;

      &.isReversed {
        grid-template-columns: 1fr;
        grid-template-areas: 'text' 'image';
      }
    }
  }

  .thumb {
    grid-area: image;
    position: relative;
    width: 100%;
    min-height: 0;
    background: radial-gradient(
      ellipse 58% 72% at 62% 42%,
      rgba(255, 122, 84, 0.14) 0%,
      rgba(255, 122, 84, 0.055) 44%,
      transparent 74%
    );

    img {
      display: block;
      width: 100%;
      height: 100%;
      object-fit: cover;
      border-radius: 28px;
      transform: scale(0.82);
      transform-origin: center;
    }

    &.anchorTop img {
      transform: translateY(-10.8%) scale(0.82);
    }

    &.anchorBottom img {
      transform: translateY(10.8%) scale(0.82);
    }

    &.anchorCenter img {
      transform: scale(0.9);
    }

    &.phoneShot.anchorTop img {
      transform: translateY(-13.5%) scale(0.76);
    }

    &.phoneShot.anchorBottom img {
      transform: translateY(13.5%) scale(0.76);
    }

    @media (max-width: 767px) {
      aspect-ratio: 16 / 11;

      img {
        border-radius: 20px;
        transform: none;
      }

      &.anchorTop img {
        transform: translateY(-6.8%);
      }

      &.anchorBottom img {
        transform: translateY(6.8%);
      }

      &.anchorCenter img {
        transform: none;
      }

      &.phoneShot.anchorTop img {
        transform: translateY(-10%);
      }

      &.phoneShot.anchorBottom img {
        transform: translateY(10%);
      }
    }
  }

  .card.isReversed .thumb {
    background: radial-gradient(
      ellipse 58% 72% at 38% 42%,
      rgba(255, 122, 84, 0.14) 0%,
      rgba(255, 122, 84, 0.055) 44%,
      transparent 74%
    );
  }

  .text {
    grid-area: text;
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 48px 42px;

    @media ${({ theme }) => theme.mediaQuery.smallTablet} {
      padding: 64px clamp(52px, 6vw, 78px);
    }

    @media (max-width: 767px) {
      padding: 40px 32px 38px;
    }
  }

  .meta {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .index {
    display: none;
  }

  .label {
    font-family: var(--font-mono), monospace;
    font-size: 11.5px;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: ${ACCENT_2};
  }

  h3 {
    margin: 20px 0 0;
    max-width: 560px;
    font-size: clamp(40px, 3.5vw, 50px);
    line-height: 1.04;
    font-weight: 570;
    letter-spacing: -0.035em;
    color: ${TEXT};
  }

  p {
    margin: 22px 0 0;
    max-width: 35rem;
    font-size: 17px;
    line-height: 1.55;
    color: rgba(245, 238, 234, 0.55);
    text-wrap: pretty;
  }

`;

const Closing = styled.div`
  position: relative;
  overflow: hidden;
  margin-top: 110px;
  border-top: 1px solid rgba(255, 146, 115, 0.18);
  background: linear-gradient(160deg, rgba(255, 122, 84, 0.14), rgba(245, 238, 234, 0.02) 46%, rgba(245, 238, 234, 0.02));

  @media ${({ theme }) => theme.mediaQuery.smallTablet} {
    margin-top: 140px;
  }
`;

const Join = styled.section`
  position: relative;
  padding: clamp(72px, 8vw, 112px) 0;
`;

const Panel = styled.div`
  position: relative;
  padding: 0 clamp(2px, 3vw, 44px);

  .glow {
    position: absolute;
    top: -250px;
    right: -170px;
    width: 680px;
    height: 680px;
    pointer-events: none;
    background: radial-gradient(closest-side, rgba(255, 122, 84, 0.3), transparent 70%);
    filter: blur(14px);
  }

  .content {
    position: relative;
    max-width: 620px;
  }

  .eyebrow {
    display: block;
    font-family: var(--font-mono), monospace;
    font-size: 11.5px;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: #ffa98d;
  }

  h2 {
    margin: 20px 0 0;
    font-size: clamp(32px, 4.2vw, 58px);
    line-height: 1.02;
    letter-spacing: -0.035em;
    font-weight: 700;

    @media (max-width: 767px) {
      font-size: clamp(40px, 3.5vw, 50px);
    }
  }

  .content > p {
    margin: 18px 0 0;
    font-size: 18px;
    line-height: 1.55;
    color: ${TEXT_2};
  }

  .fineprint {
    margin-top: 18px;
    font-family: var(--font-mono), monospace;
    font-size: 11.5px;
    letter-spacing: 0.1em;
    color: rgba(245, 238, 234, 0.35);

    @media (max-width: 767px) {
      text-align: center;
    }
  }
`;

const Footer = styled.footer`
  position: relative;
  margin-top: 0;

  ${Container} {
    padding-top: 40px;
    padding-bottom: 40px;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 20px;
    border-top: 1px solid rgba(245, 238, 234, 0.07);

    @media ${({ theme }) => theme.mediaQuery.smallTablet} {
      flex-direction: row;
      flex-wrap: wrap;
      align-items: center;
      justify-content: space-between;
      text-align: left;
      gap: 24px;
      padding-top: 56px;
      padding-bottom: 56px;
    }
  }

  .brand {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .mark {
    width: 26px;
    height: 26px;
    border-radius: 8px;
    display: block;
  }

  .wordmark {
    font-size: 13px;
    letter-spacing: 0.3em;
    text-transform: uppercase;
    color: ${TEXT_2};
  }

  .links {
    display: flex;
    flex-wrap: wrap;
    gap: 26px;
    font-size: 14px;
    color: ${TEXT_3};

    a {
      color: inherit;
    }
  }

  .copyright {
    font-family: var(--font-mono), monospace;
    font-size: 11.5px;
    color: rgba(245, 238, 234, 0.28);
  }
`;

export default MetraLayout;
